// src/utils/rankLens.ts

/**
 * Computes lens-based ranking matching the Matrix page logic
 * This should produce the same rankings as the Matrix for lens parity
 */

interface RankingContext {
  useCases: Array<{
    use_case_id: number;
    value_score: number;
    implementation_score: number;
    total_score: number;
  }>;
}

export function computeLensRank({ 
  lens, 
  valueScore, 
  easeScore, 
  totalScore,
  useCaseId,
  context
}: {
  lens: 'quickwins' | '70_30' | '50_50';
  valueScore: number;
  easeScore: number;
  totalScore: number;
  useCaseId?: number;
  context?: RankingContext;
}): number {
  // If we have context with all use cases, compute actual rank
  if (context?.useCases && useCaseId) {
    const scoredUseCases = context.useCases.map(uc => {
      let score: number;
      
      switch(lens) {
        case 'quickwins':
          // Quick wins: Distance to upper-right corner (high value, high ease)
          // Smaller distance = better rank
          const distanceToIdeal = Math.sqrt(
            Math.pow(100 - uc.value_score, 2) + 
            Math.pow(100 - uc.implementation_score, 2)
          );
          score = 100 - distanceToIdeal; // Invert so higher is better
          break;
          
        case '70_30':
          // Strategic: 70% value, 30% ease
          score = (uc.value_score * 0.7) + (uc.implementation_score * 0.3);
          break;
          
        case '50_50':
          // Balanced: 50% value, 50% ease
          score = (uc.value_score * 0.5) + (uc.implementation_score * 0.5);
          break;
          
        default:
          score = uc.total_score;
      }
      
      return {
        use_case_id: uc.use_case_id,
        score,
        // Tiebreakers: higher value, then higher ease, then use case ID
        value: uc.value_score,
        ease: uc.implementation_score
      };
    });
    
    // Sort by score (descending), then by tiebreakers
    scoredUseCases.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.value !== a.value) return b.value - a.value;
      if (b.ease !== a.ease) return b.ease - a.ease;
      return a.use_case_id - b.use_case_id; // Stable sort by ID
    });
    
    // Find the rank of the current use case
    const rank = scoredUseCases.findIndex(uc => uc.use_case_id === useCaseId) + 1;
    return rank > 0 ? rank : 1;
  }
  
  // Fallback: Return an estimated rank based on score alone
  // This is less accurate but provides a reasonable approximation
  switch(lens) {
    case 'quickwins':
      // Estimate based on distance to ideal
      const distance = Math.sqrt(
        Math.pow(100 - valueScore, 2) + 
        Math.pow(100 - easeScore, 2)
      );
      // Convert to approximate rank (1-100 scale)
      return Math.max(1, Math.round((distance / 141.42) * 100));
      
    case '70_30':
      // Use weighted score
      const strategic = (valueScore * 0.7) + (easeScore * 0.3);
      // Invert to get rank (higher score = lower rank number)
      return Math.max(1, Math.round(101 - strategic));
      
    case '50_50':
      // Use balanced score
      const balanced = (valueScore + easeScore) / 2;
      return Math.max(1, Math.round(101 - balanced));
      
    default:
      return Math.max(1, Math.round(101 - totalScore));
  }
}

export const labelLens = (lens: 'quickwins' | '70_30' | '50_50' | string): string => {
  switch(lens) {
    case 'quickwins': 
      return 'Quick Wins';
    case '70_30': 
      return '70/30 Strategic';
    case '50_50': 
      return '50/50 Balanced';
    default: 
      return 'Strategic (70/30)';
  }
};

export const describeLens = (lens: 'quickwins' | '70_30' | '50_50' | string): string => {
  switch(lens) {
    case 'quickwins': 
      return 'Prioritizes high-value, easy-to-implement use cases';
    case '70_30': 
      return 'Weights value at 70% and ease of implementation at 30%';
    case '50_50': 
      return 'Equally balances value and ease of implementation';
    default: 
      return 'Strategic prioritization based on organizational goals';
  }
};

// Helper to get lens from URL or state
export const parseLens = (lensParam?: string | null): 'quickwins' | '70_30' | '50_50' => {
  switch(lensParam?.toLowerCase()) {
    case 'quickwins':
    case 'quick-wins':
    case 'quick_wins':
      return 'quickwins';
    case '50_50':
    case '50-50':
    case 'balanced':
      return '50_50';
    case '70_30':
    case '70-30':
    case 'strategic':
    default:
      return '70_30';
  }
};